import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Table, Form, Pagination, Modal, Button } from "react-bootstrap";
import { ChevronUp, ChevronDown, Eye, Trash2, TriangleAlert } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { getConversionHistory, getDeleteConversionDetails } from "../../state/reducers/conversionHistory";

const ConversionHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { convHistoryData = [], loading, error } = useSelector((state) => state.conversionHistory);

  const [sortConfig, setSortConfig] = useState({ key: "conversion_date", direction: "desc" });
  const [dateRange, setDateRange] = useState({ fromDate: "", toDate: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [10, 25, 50, 100];
  const sortableColumns = ["conversion_date", "filename", "totalIterations", "execution_time"];
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.username) {
      dispatch(getConversionHistory({ username: user.username }));
    }
  }, [dispatch]);

  const processedConversionHistory = useMemo(() => {
    return convHistoryData.map((file) => {
      const latestIteration =
        file.iterations
          .filter(({ source_language, target_language }) => source_language && target_language)
          .sort((a, b) => new Date(b.conversion_date) - new Date(a.conversion_date))[0] ||
        file.iterations[file.iterations.length - 1];

      return {
        filename: file.filename,
        ...latestIteration,
        totalIterations: file.iterations.length,
      };
    });
  }, [convHistoryData]);

  const handleSort = (key) => {
    if (sortableColumns.includes(key)) {
      setSortConfig((prevConfig) => ({
        key,
        direction: prevConfig.key === key && prevConfig.direction === "asc" ? "desc" : "asc",
      }));
    }
  };

  const handleViewDetails = (row) => row.filename && navigate(`/conversion-details/${row.filename}`);

  const handleDeleteDetails = (row) => {
    if (row.filename) {
      setFileToDelete(row);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.username) {
        throw new Error("User not found");
      }

      await dispatch(getDeleteConversionDetails({ 
        username: user.username,
        filename: fileToDelete.filename 
      }));

      // Refresh the conversion history after successful deletion
      dispatch(getConversionHistory({ username: user.username }));
      setShowDeleteModal(false);
      setFileToDelete(null);
    } catch (error) {
      console.error("Failed to delete conversion details:", error);
      alert("Failed to delete conversion details. Please try again.");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDateChange = ({ target: { name, value } }) => {
    setDateRange((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const filteredAndSortedData = useMemo(() => {
    const filterByDate = ({ conversion_date }) => {
      const rowDate = new Date(conversion_date);
      const fromDate = dateRange.fromDate ? new Date(dateRange.fromDate) : null;
      const toDate = dateRange.toDate ? new Date(dateRange.toDate) : null;

      return (
        (!fromDate || rowDate >= fromDate) &&
        (!toDate || rowDate <= toDate)
      );
    };

    const sortData = (a, b) => {
      const { key, direction } = sortConfig;
      if (key === "conversion_date") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      if (key === "filename") {
        return direction === "asc"
          ? a[key].localeCompare(b[key])
          : b[key].localeCompare(a[key]);
      }
      return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
    };

    return processedConversionHistory.filter(filterByDate).sort(sortData);
  }, [processedConversionHistory, dateRange, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedData, currentPage, rowsPerPage]);

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  const SortIndicator = ({ columnKey }) => (
    sortableColumns.includes(columnKey) && sortConfig.key === columnKey ? (
      sortConfig.direction === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
    ) : null
  );

  if (loading) return <div>Loading...</div>;

  if (error) {
    const notFoundMessage = error.detail?.includes("Conversion history not found")
      ? "Conversion history not found"
      : `Error: ${error}`;
    return <div className="alert alert-info">{notFoundMessage}</div>;
  }

  if (!convHistoryData.length) {
    return <div className="alert alert-info">No conversion history available</div>;
  }

  return (
    <div className="page-wrapper conversion-history-page">
      <Container fluid>
        <Row>
          <Col>
            <div className="content-wrap">
              <h2 className="page-title">
                <span>Conversion History</span>
                <Link to="/code-conversion">New Conversion</Link>
              </h2>
              <div className="conversion-history-wrapper">
                <div className="token-data-detail">
                  <div className="top-row d-flex justify-content-between p-3">
                    <div className="view-page">
                      <Form.Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        style={{ width: "auto" }}
                        size="sm"
                      >
                        {rowsPerPageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Form.Select>
                      <span>entries per page</span>
                    </div>
                    <div className="search-form">
                      <Form.Group>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="fromDate"
                          value={dateRange.fromDate}
                          onChange={handleDateChange}
                          max={dateRange.toDate || undefined}
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="toDate"
                          value={dateRange.toDate}
                          onChange={handleDateChange}
                          min={dateRange.fromDate || undefined}
                        />
                      </Form.Group>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setDateRange({ fromDate: "", toDate: "" });
                          setCurrentPage(1);
                        }}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <Table striped bordered hover className="conversion-history-table">
                      <thead>
                        <tr>
                          {[
                            { key: "conversion_date", label: "Conversion Date" },
                            { key: "filename", label: "Filename" },
                            { key: "source_language", label: "Source Language" },
                            { key: "target_language", label: "Target Language" },
                            { key: "totalIterations", label: "Total Iterations" },
                            { key: "input_tokens", label: "Input Tokens" },
                            { key: "output_tokens", label: "Output Tokens" },
                            { key: "execution_time", label: "Execution Time (s)" },
                          ].map(({ key, label }) => (
                            <th
                              key={key}
                              onClick={() => handleSort(key)}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="d-flex align-items-center">
                                {label}
                                <SortIndicator columnKey={key} />
                              </div>
                            </th>
                          ))}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.map((row, index) => (
                          <tr key={index}>
                            <td className="text-center">{row.conversion_date ? new Date(row.conversion_date).toLocaleDateString() : ""}</td>
                            <td>{row.filename || ""}</td>
                            <td>{row.source_language || ""}</td>
                            <td>{row.target_language || ""}</td>
                            <td className="text-end">{row.totalIterations || 0}</td>
                            <td className="text-end">{row.input_tokens ?? ""}</td>
                            <td className="text-end">{row.output_tokens ?? ""}</td>
                            <td className="text-end">{row.execution_time?.toFixed(3) || ""}</td>
                            <td className="text-center">
                              <button
                                className="btn"
                                onClick={() => handleViewDetails(row)}
                                disabled={!row.filename}
                              >
                                <Eye size={16} strokeWidth={1.5} />
                              </button>
                              <button
                                className="btn ms-3"
                                onClick={() => handleDeleteDetails(row)}
                                disabled={!row.filename}
                              >
                                <Trash2 size={16} strokeWidth={1.5} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center p-3">
                    <div className="text-muted small">
                      Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
                    </div>
                    <Pagination>
                      <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                      <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                      {renderPaginationItems()}
                      <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                      <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered className="confirmation-modal">
        <Modal.Header closeButton>
          <TriangleAlert size={40} strokeWidth={1} />
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the conversion history for{" "}
          <strong>{fileToDelete?.filename}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button className="cancel-btn" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button className="action-btn" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConversionHistory;
